import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/layout/Dashboardlayout.jsx';
import './Exhibitordirectory.css';
import Loader from '../components/layout/preloader';

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const ExhibitorModal = ({
  mode, categories, loading,
  exName, setExName,
  exCategory,
  exBooth, setExBooth,
  exDescription, setExDescription,
  exImage, setExImage,
  onCategoryChange,
  onClose,
  onSubmit,
}) => {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    gsap.fromTo(modalRef.current,
      { opacity: 0, y: 32, scale: 0.96 },
      { opacity: 1, y: 0, scale: 1, duration: 0.35, ease: 'power3.out' }
    );
  }, []);

  const handleClose = () => {
    gsap.to(modalRef.current, { opacity: 0, y: 20, scale: 0.97, duration: 0.22, ease: 'power2.in' });
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.22, ease: 'power2.in', onComplete: onClose });
  };

  return (
    <div className="ed-modal-overlay" ref={overlayRef} onClick={handleClose}>
      <div className="ed-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <div className="ed-modal-header">
          <h2 className="ed-modal-title">{mode === 'add' ? 'ADD NEW EXHIBITOR' : 'EDIT EXHIBITOR'}</h2>
          <button className="ed-modal-close" onClick={handleClose}><CloseIcon /></button>
        </div>
        <div className="ed-modal-body">
          <div className="ed-form-group">
            <label>Brand Name <span className="ed-required">*</span></label>
            <input type="text" placeholder="Enter brand name" value={exName} onChange={(e) => setExName(e.target.value)} />
          </div>
          <div className="ed-form-group">
            <label>Category <span className="ed-required">*</span></label>
            <select value={exCategory} onChange={(e) => onCategoryChange(e.target.value)}>
              <option value="">Select category</option>
              {categories.filter(c => c.name !== 'All').map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="ed-form-group">
            <label>Booth Number <span className="ed-required">*</span></label>
            <input type="text" placeholder="e.g. A-101" value={exBooth} onChange={(e) => setExBooth(e.target.value)} />
          </div>
          <div className="ed-form-group">
            <label>Description</label>
            <textarea placeholder="Short description..." value={exDescription} onChange={(e) => setExDescription(e.target.value)} rows={3} />
          </div>
          <div className="ed-form-group">
            <label>Image URL</label>
            <input type="text" placeholder="https://..." value={exImage} onChange={(e) => setExImage(e.target.value)} />
          </div>
        </div>
        <div className="ed-modal-footer">
          <button className="ed-btn-cancel" onClick={handleClose}>Cancel</button>
          <button className="ed-btn-confirm" onClick={onSubmit} disabled={loading}>
            {loading ? 'Saving...' : mode === 'add' ? 'Add Exhibitor' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const InfoModal = ({
  infoTitle, setInfoTitle,
  infoLine1, setInfoLine1,
  infoLine2, setInfoLine2,
  loading, onClose, onSubmit,
}) => {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    gsap.fromTo(modalRef.current,
      { opacity: 0, y: 32, scale: 0.96 },
      { opacity: 1, y: 0, scale: 1, duration: 0.35, ease: 'power3.out' }
    );
  }, []);

  const handleClose = () => {
    gsap.to(modalRef.current, { opacity: 0, y: 20, scale: 0.97, duration: 0.22, ease: 'power2.in' });
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.22, ease: 'power2.in', onComplete: onClose });
  };

  return (
    <div className="ed-modal-overlay" ref={overlayRef} onClick={handleClose}>
      <div className="ed-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <div className="ed-modal-header">
          <h2 className="ed-modal-title">EDIT EXHIBITION INFO</h2>
          <button className="ed-modal-close" onClick={handleClose}><CloseIcon /></button>
        </div>
        <div className="ed-modal-body">
          <div className="ed-form-group">
            <label>Title <span className="ed-required">*</span></label>
            <input type="text" placeholder="e.g. DATES" value={infoTitle} onChange={(e) => setInfoTitle(e.target.value)} />
          </div>
          <div className="ed-form-group">
            <label>Info Line 1 <span className="ed-required">*</span></label>
            <input type="text" placeholder="e.g. May 14–30, 2026" value={infoLine1} onChange={(e) => setInfoLine1(e.target.value)} />
          </div>
          <div className="ed-form-group">
            <label>Info Line 2</label>
            <input type="text" placeholder="e.g. Oct 4-15, 2026" value={infoLine2} onChange={(e) => setInfoLine2(e.target.value)} />
          </div>
        </div>
        <div className="ed-modal-footer">
          <button className="ed-btn-cancel" onClick={handleClose}>Cancel</button>
          <button className="ed-btn-confirm" onClick={onSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ExhibitorDirectory = () => {
  const [exhibitors, setExhibitors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [exhibitionInfo, setExhibitionInfo] = useState([]);

  const [showAddExhibitor, setShowAddExhibitor] = useState(false);
  const [showEditExhibitor, setShowEditExhibitor] = useState(false);
  const [showEditInfo, setShowEditInfo] = useState(false);

  const [editExhibitor, setEditExhibitor] = useState(null);
  const [editInfo, setEditInfo] = useState(null);

  const [exName, setExName] = useState('');
  const [exCategory, setExCategory] = useState('');
  const [exCategorySlug, setExCategorySlug] = useState('');
  const [exCategoryId, setExCategoryId] = useState('');
  const [exBooth, setExBooth] = useState('');
  const [exDescription, setExDescription] = useState('');
  const [exImage, setExImage] = useState('');

  const [infoTitle, setInfoTitle] = useState('');
  const [infoLine1, setInfoLine1] = useState('');
  const [infoLine2, setInfoLine2] = useState('');

  const [loading, setLoading] = useState(false);

  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const sectionsRef = useRef([]);

  const fetchAll = async () => {
    const { data: exData } = await supabase.from('exhibitors').select('*').order('id');
    const { data: catData } = await supabase.from('categories').select('*').order('id');
    const { data: infoData } = await supabase.from('exhibition_info').select('*').order('display_order');
    setExhibitors(exData || []);
    setCategories(catData || []);
    setExhibitionInfo(infoData || []);
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', delay: 4 }
      );
      gsap.fromTo(sectionsRef.current,
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.12, delay: 4.15 }
      );
    }, pageRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (exhibitors.length === 0) return;
    const rows = document.querySelectorAll('.ed-table tbody tr');
    gsap.fromTo(rows,
      { opacity: 0, x: -12 },
      { opacity: 1, x: 0, duration: 0.3, stagger: 0.04, ease: 'power2.out', delay: 4 }
    );
  }, [exhibitors]);

  const handleCategoryChange = useCallback((catName) => {
    setExCategory(catName);
    const found = categories.find(c => c.name === catName);
    if (found) {
      setExCategorySlug(found.slug);
      setExCategoryId(String(found.id));
    } else {
      setExCategorySlug('');
      setExCategoryId('');
    }
  }, [categories]);

  const resetExhibitorForm = useCallback(() => {
    setExName('');
    setExCategory('');
    setExCategorySlug('');
    setExCategoryId('');
    setExBooth('');
    setExDescription('');
    setExImage('');
  }, []);

  const addExhibitor = async () => {
    if (!exName || !exCategory || !exBooth) return;
    setLoading(true);
    await supabase.from('exhibitors').insert([{
      name: exName,
      category: exCategory,
      category_slug: exCategorySlug,
      category_id: exCategoryId,
      location_id: exBooth,
      description: exDescription,
      img_ee: exImage || null,
      published: false,
    }]);
    resetExhibitorForm();
    setShowAddExhibitor(false);
    await fetchAll();
    setLoading(false);
  };

  const updateExhibitor = async () => {
    if (!exName || !exCategory || !exBooth) return;
    setLoading(true);
    await supabase.from('exhibitors').update({
      name: exName,
      category: exCategory,
      category_slug: exCategorySlug,
      category_id: exCategoryId,
      location_id: exBooth,
      description: exDescription,
      img_ee: exImage || null,
    }).eq('id', editExhibitor.id);
    resetExhibitorForm();
    setShowEditExhibitor(false);
    setEditExhibitor(null);
    await fetchAll();
    setLoading(false);
  };

  const deleteExhibitor = async (id) => {
    const row = document.querySelector(`[data-exhibitor-id="${id}"]`);
    if (row) {
      await gsap.to(row, { opacity: 0, x: 20, duration: 0.25, ease: 'power2.in' });
    }
    await supabase.from('exhibitors').delete().eq('id', id);
    await fetchAll();
  };

  const togglePublished = async (exhibitor) => {
    await supabase.from('exhibitors').update({ published: !exhibitor.published }).eq('id', exhibitor.id);
    await fetchAll();
  };

  const openEditExhibitor = (ex) => {
    setEditExhibitor(ex);
    setExName(ex.name || '');
    setExCategory(ex.category || '');
    setExCategorySlug(ex.category_slug || '');
    setExCategoryId(ex.category_id || '');
    setExBooth(ex.location_id || '');
    setExDescription(ex.description || '');
    setExImage(ex.img_ee || '');
    setShowEditExhibitor(true);
  };

  const updateInfo = async () => {
    setLoading(true);
    await supabase.from('exhibition_info').update({
      title: infoTitle,
      info_text_line1: infoLine1,
      info_text_line2: infoLine2,
    }).eq('id', editInfo.id);
    setShowEditInfo(false);
    setEditInfo(null);
    setInfoTitle('');
    setInfoLine1('');
    setInfoLine2('');
    await fetchAll();
    setLoading(false);
  };

  const deleteInfo = async (id) => {
    await supabase.from('exhibition_info').delete().eq('id', id);
    await fetchAll();
  };

  const openEditInfo = (row) => {
    setEditInfo(row);
    setInfoTitle(row.title || '');
    setInfoLine1(row.info_text_line1 || '');
    setInfoLine2(row.info_text_line2 || '');
    setShowEditInfo(true);
  };

  const handleAddBtnClick = (e) => {
    gsap.fromTo(e.currentTarget, { scale: 0.93 }, { scale: 1, duration: 0.3, ease: 'elastic.out(1, 0.5)' });
    resetExhibitorForm();
    setShowAddExhibitor(true);
  };

  return (
    <>
      <Loader />
      <DashboardLayout activePath="/dashboard/exhibitors">
        <div className="ed-page" ref={pageRef}>

          <div className="ed-page-header" ref={headerRef}>
            <div>
              <h1 className="ed-page-title">EXHIBITOR DIRECTORY</h1>
              <p className="ed-page-subtitle">Manage all exhibitors and booth assignments</p>
            </div>
            <button className="ed-btn-add" onClick={handleAddBtnClick}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add New Exhibitor
            </button>
          </div>

          <div className="ed-section" ref={(el) => (sectionsRef.current[0] = el)}>
            <div className="ed-table-wrapper">
              <table className="ed-table">
                <colgroup>
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>IMAGE</th>
                    <th>NAME</th>
                    <th>CATEGORY</th>
                    <th>BOOTH</th>
                    <th>DESCRIPTION</th>
                    <th>PUBLISHED</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {exhibitors.map((ex) => (
                    <tr key={ex.id} data-exhibitor-id={ex.id}>
                      <td>
                        <div className="ed-img-cell">
                          {ex.img_ee ? (
                            <img src={ex.img_ee} alt={ex.name} className="ed-thumb" />
                          ) : (
                            <div className="ed-thumb-placeholder">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                              </svg>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="ed-name">{ex.name}</td>
                      <td><span className="ed-badge">{ex.category}</span></td>
                      <td className="ed-booth">{ex.location_id}</td>
                      <td className="ed-desc">{ex.description}</td>
                      <td>
                        <div className="ed-publish-cell">
                          <button
                            className={`ed-toggle ${ex.published ? 'ed-toggle--on' : 'ed-toggle--off'}`}
                            onClick={() => togglePublished(ex)}
                            title={ex.published ? 'Click to unpublish' : 'Click to publish'}
                          >
                            <span className="ed-toggle-knob" />
                          </button>
                          <span className={`ed-publish-label ${ex.published ? 'ed-publish-label--on' : 'ed-publish-label--off'}`}>
                            {ex.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="ed-actions">
                          <button className="ed-action-btn ed-edit-btn" onClick={() => openEditExhibitor(ex)}><EditIcon /></button>
                          <button className="ed-action-btn ed-delete-btn" onClick={() => deleteExhibitor(ex.id)}><TrashIcon /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="ed-section" ref={(el) => (sectionsRef.current[1] = el)}>
            <div className="ed-section-header">
              <h2 className="ed-section-title">EXHIBITION INFO</h2>
            </div>
            <div className="ed-table-wrapper">
              <table className="ed-table">
                <colgroup>
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '35%' }} />
                  <col style={{ width: '35%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>TITLE</th>
                    <th>INFO LINE 1</th>
                    <th>INFO LINE 2</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {exhibitionInfo.map((row) => (
                    <tr key={row.id}>
                      <td className="ed-info-title">{row.title}</td>
                      <td>{row.info_text_line1}</td>
                      <td>{row.info_text_line2}</td>
                      <td>
                        <div className="ed-actions">
                          <button className="ed-action-btn ed-edit-btn" onClick={() => openEditInfo(row)}><EditIcon /></button>
                          <button className="ed-action-btn ed-delete-btn" onClick={() => deleteInfo(row.id)}><TrashIcon /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {showAddExhibitor && (
            <ExhibitorModal
              mode="add"
              categories={categories}
              loading={loading}
              exName={exName} setExName={setExName}
              exCategory={exCategory}
              exBooth={exBooth} setExBooth={setExBooth}
              exDescription={exDescription} setExDescription={setExDescription}
              exImage={exImage} setExImage={setExImage}
              onCategoryChange={handleCategoryChange}
              onClose={() => { setShowAddExhibitor(false); resetExhibitorForm(); }}
              onSubmit={addExhibitor}
            />
          )}

          {showEditExhibitor && (
            <ExhibitorModal
              mode="edit"
              categories={categories}
              loading={loading}
              exName={exName} setExName={setExName}
              exCategory={exCategory}
              exBooth={exBooth} setExBooth={setExBooth}
              exDescription={exDescription} setExDescription={setExDescription}
              exImage={exImage} setExImage={setExImage}
              onCategoryChange={handleCategoryChange}
              onClose={() => { setShowEditExhibitor(false); resetExhibitorForm(); setEditExhibitor(null); }}
              onSubmit={updateExhibitor}
            />
          )}

          {showEditInfo && (
            <InfoModal
              infoTitle={infoTitle} setInfoTitle={setInfoTitle}
              infoLine1={infoLine1} setInfoLine1={setInfoLine1}
              infoLine2={infoLine2} setInfoLine2={setInfoLine2}
              loading={loading}
              onClose={() => { setShowEditInfo(false); setEditInfo(null); }}
              onSubmit={updateInfo}
            />
          )}

        </div>
      </DashboardLayout>
    </>
  );
};

export default ExhibitorDirectory;